import { keepPreviousData, useInfiniteQuery as useRqInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
    CreateProjectModel,
    ProjectModel,
    ProjectStatusModel,
} from '@mesterplan/shared-types';
import type {
    ApiError,
    MesterplanApi,
    ProjectDto,
    ProjectStatus,
    ProjectType,
    ProjectDetails,
    CreateProjectPayload,
    UpdateProjectPayload,
    PartnerInvitationDto,
    ProjectLookupDto,
    ListProjectsQuery,
} from '@mesterplan/api-client';
import { formatCaseNumber } from '@mesterplan/shared-utils';
import { useFirebase } from '../context';
import { CACHE_KEYS, NotificationCallbacks, CompanyContext } from '../types';
import { UseInfiniteQueryResult } from './useInfiniteQuery';

/**
 * Blank-safe coalesce. `??` treats an empty string as a present value, so a row whose
 * `caseNumber` migrated across as `''` (rather than NULL) would shadow the `number`
 * fallback and leave the project unsearchable by sagsnummer.
 */
function firstNonBlank(...values: (string | null | undefined)[]): string | undefined {
    for (const value of values) {
        if (typeof value === 'string' && value.trim().length > 0) {
            return value;
        }
    }
    return undefined;
}

const DOTNET_PROJECTS_PAGE_SIZE = 200;

/** Page size for pickers: small enough to open instantly, large enough to rarely need page 2. */
export const PROJECT_PICKER_PAGE_SIZE = 100;

/**
 * Next-page cursor for the infinite projects list. Terminates when the server returns a
 * partial (or empty) RAW page — `rawCount` is the pre-archived-filter count, so it is the
 * true end-of-data signal. Keying off rawCount (not filtered-items vs total) means an
 * all-archived page still advances instead of stalling, and there is no infinite fetch loop
 * when filtered items never reach `total`. Exported for unit testing. (#1365 / PR review)
 */
export function computeNextProjectsPageParam(
    lastPage: { rawCount: number; page: number },
    pageSize: number,
): number | undefined {
    return lastPage.rawCount >= pageSize ? lastPage.page + 1 : undefined;
}

export interface ProjectSearchPage {
    items: ProjectModel[];
    page: number;
    pageSize: number;
    total: number;
    /** Pre-archived-filter count — see {@link computeNextProjectsPageParam}. */
    rawCount: number;
}

export interface UseProjectSearchOptions {
    /**
     * Sent to `GET /api/projects?search=` — matched server-side on Title, Number,
     * CustomerName, Location and CaseNumber.
     */
    search: string;
    /** Nothing is fetched until this is true — pass the picker's open state. */
    enabled: boolean;
    pageSize?: number;
    /**
     * Root-level projects only (no parentProjectId) — for pickers that used to filter
     * `!parentProjectId` over a drained list (e.g. the quote's "Overordnet projekt" picker).
     */
    parentOnly?: boolean;
    /**
     * Matches any of the given stages — e.g. the planner's status chips. Empty or absent
     * means no filter, the same rule as `filterProjectsByStatus`.
     */
    statuses?: readonly ProjectStatusModel[];
}

const API_STATUS_FOR_MODEL: Record<ProjectStatusModel, ProjectStatus> = {
    upcoming: 'Planned',
    'in-progress': 'Active',
    completed: 'Completed',
    cancelled: 'Cancelled',
};

/**
 * The frontend stages as the .NET `ProjectStatus` names the list endpoint filters on —
 * the inverse of `mapApiStatusToModel` (Archived has no stage; pickers never ask for it).
 * Sorted, so the same chip set always yields the same query key.
 */
export function mapModelStatusesToApi(statuses: readonly ProjectStatusModel[]): ProjectStatus[] {
    return Array.from(new Set(statuses.map((s) => API_STATUS_FOR_MODEL[s]))).sort();
}

/**
 * The `GET /api/projects` query for one picker page. Optional filters are omitted rather
 * than sent empty: a blank `search` would still be an ILike over every row, and an empty
 * `statuses` means "no filter", which is what leaving it out already says.
 */
export function buildProjectSearchQuery({
    page,
    pageSize,
    term,
    parentOnly,
    apiStatuses,
}: {
    page: number;
    pageSize: number;
    term: string;
    parentOnly: boolean;
    apiStatuses: readonly ProjectStatus[];
}): ListProjectsQuery {
    return {
        page,
        pageSize,
        ...(term ? { search: term } : {}),
        ...(parentOnly ? { rootOnly: true } : {}),
        ...(apiStatuses.length > 0 ? { statuses: apiStatuses } : {}),
    };
}

/**
 * Whether a project update needs a follow-up status call.
 *
 * Keyed on the status DEFINITION id, never on the stage label: two custom statuses can
 * share a stage (e.g. both Active-backed), so comparing stages silently drops a real
 * status change. That bug shipped twice — once here and once in the app's own copy of
 * this hook — which is why this predicate is extracted and tested rather than inlined.
 */
export function shouldChangeProjectStatus(
    nextStatusDefinitionId: string | undefined,
    currentStatusDefinitionId: string | undefined,
): boolean {
    return Boolean(nextStatusDefinitionId) && nextStatusDefinitionId !== currentStatusDefinitionId;
}

/**
 * Turns a .NET ApiError into a human-readable message. A 400 from the
 * CreateProject/UpdateProject validators carries field messages in `errors`
 * (e.g. CustomerId → "Customer does not exist…"); surfacing those beats the
 * opaque "One or more validation errors occurred." default. Falls back to
 * `detail`, then `title`, then the caller's generic message.
 */
/**
 * React Query aborts a query's signal on unmount and on key change. The resulting
 * rejection is the caller losing interest, not a fetch failure, so it must not be
 * reported to the user — otherwise every navigation away from a project screen
 * pops an error toast (#2196).
 */
function isAbortError(error: unknown): boolean {
    return error instanceof Error && error.name === 'AbortError';
}

/**
 * Not `new DOMException(...)`: this package is shared with the React Native
 * clients, which have no DOM. A plain Error carrying the conventional name is
 * portable and is what {@link isAbortError} matches on.
 */
function newAbortError(): Error {
    const error = new Error('Aborted');
    error.name = 'AbortError';
    return error;
}

export const PROJECT_VERSION_CONFLICT_CODE = 'project_version_conflict';

export const PROJECT_VERSION_CONFLICT_MESSAGE =
    'Sagen er blevet ændret af en anden, siden du åbnede den. Luk og åbn den igen for at se ændringerne.';

/** Thrown by the update mutation when the API refuses a stale `version` (409). */
export class ProjectVersionConflictError extends Error {
    constructor() {
        super(PROJECT_VERSION_CONFLICT_MESSAGE);
        this.name = 'ProjectVersionConflictError';
    }
}

function formatApiError(error: ApiError | undefined, fallback: string): string {
    const fieldMessages = error?.errors
        ? Object.values(error.errors).flat().filter((m) => !!m?.trim())
        : [];
    if (fieldMessages.length > 0) {
        return fieldMessages.join('\n');
    }
    return error?.detail ?? error?.title ?? fallback;
}

/**
 * Projects are .NET-only. Every read and write below goes through `api.projects.*`
 * (and the supporting partner-invitation / time-entry / material readers); there is
 * no Firestore path. The Projects page (Projects.tsx / ProjectDetails.tsx) fires
 * zero `firestore.googleapis.com` requests as a result.
 *
 * Net-new projects are always created in .NET with a Guid id; the formerly-hybrid
 * Firestore push-id branch (legacy/test data) has been removed.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Enum translation. `ProjectStatusModel` is the .NET ProjectStatus enum in the
// frontend's spelling; the two now correspond one-to-one (Archived is filtered
// out on read). What the user sees is the status *definition* carried alongside
// on statusName / statusColor — see mapApiProject below.
// ─────────────────────────────────────────────────────────────────────────────
function mapApiStatusToModel(status: ProjectStatus): ProjectStatusModel {
    switch (status) {
        case 'Active':
            return 'in-progress';
        case 'Completed':
            return 'completed';
        case 'Cancelled':
        case 'Archived':
            return 'cancelled';
        default:
            return 'upcoming';
    }
}

function mapApiTypeToModel(type: ProjectType): ProjectModel['projectType'] {
    switch (type) {
        case 'NewBuild':
            return 'NEW_BUILD';
        case 'Repair':
            return 'REPAIR';
        case 'Maintenance':
            return 'MAINTENANCE';
        case 'Consultation':
            return 'CONSULTATION';
        case 'Other':
            return 'OTHER';
        default:
            return 'RENOVATION';
    }
}

function mapModelTypeToApi(type: ProjectModel['projectType'] | undefined): ProjectType {
    switch (type) {
        case 'NEW_BUILD':
            return 'NewBuild';
        case 'REPAIR':
            return 'Repair';
        case 'MAINTENANCE':
            return 'Maintenance';
        case 'CONSULTATION':
            return 'Consultation';
        case 'OTHER':
            return 'Other';
        default:
            return 'Renovation';
    }
}

function toDateOnly(value: string | null | undefined): string | null {
    if (!value) {
        return null;
    }
    return value.includes('T') ? value.slice(0, 10) : value;
}

/**
 * Maps a .NET `ProjectDto` to the canonical `ProjectModel`. #702 flattened the
 * full TS field set onto the DTO, so every modelled field is read from the DTO —
 * the DTO is the single source of truth post-cutover.
 */
export function mapApiProject(dto: ProjectDto, companyId: string): ProjectModel {
    return {
        id: dto.id,
        companyId,
        title: dto.title,
        clientId: dto.customerId,
        clientName: dto.customerName ?? '',
        description: dto.description ?? '',
        notes: dto.notes ?? '',
        timeplanNotes: dto.timeplanNotes ?? undefined,
        status: mapApiStatusToModel(dto.status),
        statusDefinitionId: dto.statusDefinitionId ?? undefined,
        statusName: dto.statusName ?? undefined,
        statusColor: dto.statusColor ?? undefined,
        price: dto.price ?? dto.budgetExclVat ?? 0,
        startDate: dto.startDate ?? null,
        endDate: dto.endDate ?? null,
        createdById: dto.createdById ?? '',
        createdByName: dto.createdByName ?? '',
        createdAt: dto.createdAt,
        updatedAt: dto.updatedAt,
        caseNumber: firstNonBlank(dto.caseNumber, dto.number),

        // Ejendom link — undefined when the project still carries only a free-text address.
        propertyId: dto.propertyId ?? undefined,
        propertyUnitId: dto.propertyUnitId ?? undefined,
        assets: dto.assets?.length ? dto.assets : undefined,
        billingCustomerIdOverride: dto.billingCustomerIdOverride ?? undefined,
        billTo: dto.billTo ?? undefined,
        generatedFromAgreement: dto.generatedFromAgreement ?? undefined,

        // People (denormalized parallel arrays)
        projectLeaderIds: dto.projectLeaderIds ?? undefined,
        projectLeaderNames: dto.projectLeaderNames ?? undefined,
        assignedEmployeeIds: dto.assignedEmployeeIds ?? undefined,
        assignedEmployeeNames: dto.assignedEmployeeNames ?? undefined,

        // Pricing & estimate
        hourlyRate: dto.hourlyRate ?? undefined,
        estimatedHours: dto.estimatedHours ?? undefined,
        includeVAT: dto.includeVat ?? undefined,
        location: dto.location ?? undefined,
        projectType: mapApiTypeToModel(dto.type),

        // KLS / budget / commercial
        klsSettings: dto.klsSettings as ProjectModel['klsSettings'],
        budgetEnabled: dto.budgetEnabled ?? undefined,
        budgetId: dto.budgetId ?? undefined,
        isCommercialProject: dto.isCommercialProject ?? undefined,
        commercialSocialCostMultiplier: dto.commercialSocialCostMultiplier ?? undefined,
        version: dto.version,
        economyMetrics: dto.economyMetrics
            ? {
                  quoteTotal: dto.economyMetrics.quoteTotal,
                  startupTotal: dto.economyMetrics.startupTotal,
                  actualSpent: dto.economyMetrics.actualSpent,
                  actualRemaining: dto.economyMetrics.actualRemaining,
                  aftalesedlerTotal: dto.economyMetrics.aftalesedlerTotal,
                  dgPercent: dto.economyMetrics.dgPercent,
                  dbAmount: dto.economyMetrics.dbAmount,
                  lastUpdated: dto.economyMetrics.lastUpdated ?? '',
              }
            : undefined,

        // Progress tracking
        expectedProgress: dto.expectedProgress ?? undefined,
        expectedProgressManualOverride: dto.expectedProgressManualOverride ?? undefined,
        actualProgress: dto.actualProgress ?? undefined,
        actualProgressUpdatedAt: dto.actualProgressUpdatedAt ?? undefined,

        // Guest sharing
        guestKsToken: dto.guestKsToken ?? undefined,

        // Dinero / e-conomic
        dineroContactGuid: dto.dineroContactGuid ?? undefined,
        dineroInvoiceGuids: dto.dineroInvoiceGuids ?? undefined,
        economicCustomerNumber: dto.economicCustomerNumber ?? undefined,
        economicInvoiceNumbers: dto.economicInvoiceNumbers ?? undefined,
        syncedFromEconomic: dto.syncedFromEconomic ?? undefined,

        // Derived financial caches
        actualRevenue: dto.actualRevenue ?? undefined,
        materialCosts: dto.materialCosts ?? undefined,
        laborCosts: dto.laborCosts ?? undefined,
        totalCosts: dto.totalCosts ?? undefined,
        profit: dto.profit ?? undefined,
        profitMargin: dto.profitMargin ?? undefined,
        actualHours: dto.actualHours ?? undefined,
        profitPerHour: dto.profitPerHour ?? undefined,

        // Sub-project hierarchy (server-owned)
        parentProjectId: dto.parentProjectId ?? undefined,
        parentCaseNumber: dto.parentCaseNumber ?? undefined,
        rootProjectId: dto.rootProjectId ?? undefined,
        depth: dto.depth ?? undefined,
        childOrder: dto.childOrder ?? undefined,
        hasChildren: dto.hasChildren,
        childCount: dto.childCount,
    };
}

/** Danish VAT multiplier used only for the informational incl.-VAT budget column. */
const DANISH_VAT_MULTIPLIER = 1.25;

/**
 * `Project.Price` is the sales price EXCL. VAT (quote conversion sets it to the quote subtotal,
 * and the case economy reads it as such), so the UI price goes into `budgetExclVat` as is and
 * the incl.-VAT column is derived from it. This used to be the other way round, which stored
 * the excl.-VAT price as "incl. VAT" and divided it by 1.25 again.
 */
export function priceToBudgetFields(price: number | null | undefined): {
    budgetExclVat: number | null;
    budgetInclVat: number | null;
} {
    if (price == null) return { budgetExclVat: null, budgetInclVat: null };
    return {
        budgetExclVat: price,
        budgetInclVat: Number((price * DANISH_VAT_MULTIPLIER).toFixed(2)),
    };
}

/**
 * Builds the `ProjectDetails` carrier from the canonical model. Carries the full
 * extended field set so create/update are non-lossy. Server-derived caches
 * (financials) are sent only when present so a plain edit can't blank them.
 */
/**
 * Maps the UI's `CreateProjectModel` onto the create payload.
 *
 * Exported and pure so the hook and its tests exercise the same code rather than two
 * copies of it - the same reason `shouldChangeProjectStatus` lives out here.
 *
 * Worth knowing why this exists at all: `propertyId` was declared on both
 * `CreateProjectModel` and `CreateProjectPayload`, and the mapping in between simply
 * never copied it. Nothing failed - the field was silently dropped on every create, so a
 * project could only be linked to an ejendom by opening it again and editing it. A field
 * that exists at both ends and vanishes in the middle is invisible to the type checker,
 * which is exactly the kind of gap worth pinning with a test.
 */
/**
 * Update-payload fragment for "Faktureres til": undefined leaves the stored override alone
 * (no key), null clears it, a string sets it. Mirrors the backend Patch<Guid?>.
 */
export function billToOverrideUpdateFields(
    billingCustomerIdOverride: string | null | undefined,
): { billingCustomerIdOverride?: string | null } {
    return billingCustomerIdOverride !== undefined ? { billingCustomerIdOverride } : {};
}

export function buildCreateProjectPayload(data: CreateProjectModel): CreateProjectPayload {
    return {
        title: data.title.trim(),
        type: mapModelTypeToApi(data.projectType),
        customerId: data.clientId,
        startDate: toDateOnly(data.startDate),
        endDate: toDateOnly(data.endDate),
        description: data.description?.trim() || null,
        ...priceToBudgetFields(data.price),
        currency: 'DKK',
        // The key is left OFF entirely unless the admin overrode the suggestion, so the server
        // allocates from its counter (ConsumeNextAsync). Sending an explicit null would behave
        // the same server-side, but the absent key is the documented contract here.
        ...(data.number?.trim() ? { number: data.number.trim() } : {}),
        // What makes the new project a delprojekt. Declared on CreateProjectModel, accepted
        // by CreateProjectRequest and acted on by CreateProjectHandler (it derives number,
        // depth, childOrder and rootProjectId from the parent) — and, until this line, never
        // copied between the two ends, so every caller asking for a delprojekt silently got a
        // root project instead.
        parentProjectId: data.parentProjectId || null,
        // The NemHandel `caseNumber` still rides in details.
        details: toProjectDetails(data),
        // Ejendom link. Optional at create - a project booked against a building carries
        // it from the start, so nobody has to reopen the job to attach the address the
        // technician was sent to.
        propertyId: data.propertyId || null,
        propertyUnitId: data.propertyUnitId || null,
        // Only sent when someone other than the customer is invoiced; absent keeps the default.
        ...(data.billingCustomerIdOverride ? { billingCustomerIdOverride: data.billingCustomerIdOverride } : {}),
        // Anlæg to link at create (a serviceordre). Left off when empty so other callers' payloads are unchanged.
        ...(data.assetIds?.length ? { assetIds: data.assetIds } : {}),
    };
}

function toProjectDetails(data: Partial<CreateProjectModel> & Partial<ProjectModel>): ProjectDetails {
    return {
        notes: data.notes ?? null,
        timeplanNotes: data.timeplanNotes ?? null,
        location: data.location ?? null,
        caseNumber: data.caseNumber ?? null,
        parentCaseNumber: data.parentCaseNumber ?? null,
        price: data.price ?? null,
        hourlyRate: data.hourlyRate ?? null,
        estimatedHours: data.estimatedHours ?? null,
        includeVat: data.includeVAT ?? null,
        projectLeaderIds: data.projectLeaderIds ?? null,
        projectLeaderNames: data.projectLeaderNames ?? null,
        assignedEmployeeIds: data.assignedEmployeeIds ?? null,
        assignedEmployeeNames: data.assignedEmployeeNames ?? null,
        budgetEnabled: data.budgetEnabled ?? null,
        budgetId: data.budgetId ?? null,
        klsSettings: data.klsSettings ?? null,
        isCommercialProject: data.isCommercialProject ?? null,
        commercialSocialCostMultiplier: data.commercialSocialCostMultiplier ?? null,
        economyMetrics: data.economyMetrics ?? null,
        expectedProgress: data.expectedProgress ?? null,
        expectedProgressManualOverride: data.expectedProgressManualOverride ?? null,
        actualProgress: data.actualProgress ?? null,
        actualProgressUpdatedAt: data.actualProgressUpdatedAt ?? null,
        guestKsToken: data.guestKsToken ?? null,
        dineroContactGuid: data.dineroContactGuid ?? null,
        dineroInvoiceGuids: data.dineroInvoiceGuids ?? null,
        economicCustomerNumber: data.economicCustomerNumber ?? null,
        economicInvoiceNumbers: data.economicInvoiceNumbers ?? null,
        syncedFromEconomic: data.syncedFromEconomic ?? null,
        actualRevenue: data.actualRevenue ?? null,
        materialCosts: data.materialCosts ?? null,
        laborCosts: data.laborCosts ?? null,
        totalCosts: data.totalCosts ?? null,
        profit: data.profit ?? null,
        profitMargin: data.profitMargin ?? null,
        actualHours: data.actualHours ?? null,
        profitPerHour: data.profitPerHour ?? null,
    };
}

interface UseProjectOperationsOptions {
    notifications?: NotificationCallbacks;
    selectedCompany?: CompanyContext | null;
}

export function useProjectOperations(options: UseProjectOperationsOptions = {}) {
    const queryClient = useQueryClient();
    const { api } = useFirebase();
    const { notifications, selectedCompany } = options;

    // ---- .NET readers ----

    const listApiProjects = async (
        queryOverrides: {
            search?: string;
            parentProjectId?: string;
            rootProjectId?: string;
            fromDate?: string;
            toDate?: string;
            involvedUserId?: string;
        } = {},
        signal?: AbortSignal,
    ): Promise<ProjectModel[] | null> => {
        if (!api || !selectedCompany?.id) {
            return null;
        }
        const items: ProjectModel[] = [];
        let page = 1;
        // Paginate the full set; the company-scoped list is bounded server-side.
        // eslint-disable-next-line no-constant-condition
        while (true) {
            // Abort between pages as well as during one: a drain that is no longer
            // wanted must stop at the next page boundary rather than run to the end
            // of the table. Without this the loop kept firing after unmount (#2196).
            if (signal?.aborted) {
                throw newAbortError();
            }
            const response = await api.projects.list({
                page,
                pageSize: DOTNET_PROJECTS_PAGE_SIZE,
                search: queryOverrides.search?.trim() || undefined,
                parentProjectId: queryOverrides.parentProjectId,
                rootProjectId: queryOverrides.rootProjectId,
                fromDate: queryOverrides.fromDate,
                toDate: queryOverrides.toDate,
                involvedUserId: queryOverrides.involvedUserId,
            }, { signal });
            if (!response.ok) {
                // A failed page is a real error, not "no projects" — throw so the reader hook's
                // catch surfaces it (onError / isError) instead of silently returning an empty list.
                throw new Error(formatApiError(response.error, 'Fejl ved hentning af projekter'));
            }
            // Archived = soft-deleted; never surface it in the lists.
            items.push(
                ...response.data.items
                    .filter((dto) => dto.status !== 'Archived')
                    .map((dto) => mapApiProject(dto, selectedCompany.id)),
            );
            if (response.data.page * response.data.pageSize >= response.data.total) {
                break;
            }
            page += 1;
        }
        // rootProjectId is now filtered server-side (it used to drain every page and
        // filter here, which cost 14 requests / 5.7 MB on a 7.700-project tenant just to
        // open one project). The server matches descendants only, exactly as this did.
        return items;
    };

    const getApiProject = async (projectId: string): Promise<ProjectModel | undefined> => {
        if (!api || !selectedCompany?.id) {
            return undefined;
        }
        const response = await api.projects.get(projectId);
        if (!response.ok) {
            // 404 is a genuine "not found" (→ undefined); any other status is a real fetch
            // failure and must not be coerced to not-found (which would both mislabel the error
            // AND, for the update flow, drop the merge base → a lossy full-replace PUT). Throw.
            if (response.error?.status === 404) {
                return undefined;
            }
            throw new Error(formatApiError(response.error, 'Fejl ved hentning af projekt'));
        }
        // Treat archived (soft-deleted) projects as not found.
        if (response.data.status === 'Archived') {
            return undefined;
        }
        return mapApiProject(response.data, selectedCompany.id);
    };

    // ---- case numbers ----

    /**
     * The .NET server allocates the project number on create
     * (CreateProjectHandler.ConsumeNextAsync), so the client preview is no longer
     * computed here. The authoritative number always comes back on the created
     * project. Kept as an empty preview to preserve the hook surface.
     */
    const useNextCaseNumber = () => {
        return useQuery({
            queryKey: ['NEXT_CASE_NUMBER', selectedCompany?.id],
            // The org's configured counter (Indstillinger -> Sagsnummer), formatted the same
            // way the server will format it on create. A hint, not a reservation: the counter
            // is only consumed when a project is actually created, so two people opening the
            // dialog see the same suggestion and still get distinct numbers on save.
            queryFn: async (): Promise<string> => {
                if (!api) {
                    return '';
                }
                const result = await api.settings.getCaseNumber();
                if (!result.ok) {
                    // A missing hint is not worth failing project creation over.
                    return '';
                }
                return formatCaseNumber(result.data.prefix, result.data.nextNumber);
            },
            enabled: !!api && !!selectedCompany?.id,
        });
    };

    /**
     * Applies a non-default initial status to a freshly-created .NET project.
     * Create always stamps the org's Planned *default definition*
     * (CreateProjectHandler.AssignInitialStatusDefinition); the create dialog lets the
     * user pick a start status, so we follow up with changeStatus — but only when the
     * choice actually differs from what create already assigned. Calling changeStatus
     * with the id the project already has is a no-op the backend rejects as a conflict
     * (ChangeProjectStatusHandler: same stage + same definition id), which would
     * otherwise surface a false "status could not be set" error on the most common
     * first action (picking the default status in the create dialog).
     */
    const applyInitialApiStatus = async (
        apiClient: MesterplanApi,
        projectId: string,
        statusDefinitionId: string | undefined,
    ): Promise<boolean> => {
        if (!statusDefinitionId) {
            return true; // nothing to apply
        }
        const created = await getApiProject(projectId);
        if (!shouldChangeProjectStatus(statusDefinitionId, created?.statusDefinitionId)) {
            return true; // create already assigned this exact status definition
        }
        // Return whether the follow-up status change succeeded rather than throwing. The project
        // is ALREADY created (and its case number consumed) at this point, so rejecting the whole
        // create would make the user retry → a duplicate project. The caller reports a partial
        // success instead ("oprettet, men status kunne ikke sættes").
        try {
            const response = await apiClient.projects.changeStatus(projectId, { statusDefinitionId });
            if (!response.ok) {
                console.error('Error applying initial project status:', response.error);
                return false;
            }
            return true;
        } catch (error) {
            console.error('Error applying initial project status:', error);
            return false;
        }
    };

    const useCreateProject = () => {
        return useMutation({
            mutationFn: async (data: CreateProjectModel): Promise<string> => {
                if (!selectedCompany?.id) {
                    // Throw (not return) so a bare resolve doesn't fire the "Projekt oprettet" toast.
                    notifications?.onError?.('Ingen virksomhed valgt');
                    throw new Error('Ingen virksomhed valgt');
                }

                if (!api) {
                    notifications?.onError?.('Fejl ved oprettelse af projekt');
                    throw new Error('API client unavailable');
                }

                try {
                    const payload = buildCreateProjectPayload(data);
                    const response = await api.projects.create(payload);
                    if (!response.ok) {
                        throw new Error(formatApiError(response.error, 'Projekt kunne ikke oprettes'));
                    }
                    const projectId = response.data;
                    const statusApplied = await applyInitialApiStatus(api, projectId, data.statusDefinitionId);
                    if (!statusApplied) {
                        // Created, but the chosen status didn't stick — surface it WITHOUT failing
                        // the create (onSuccess still fires), so the user doesn't retry and duplicate.
                        notifications?.onError?.(
                            'Projektet blev oprettet, men den valgte status kunne ikke sættes. Ret status manuelt.',
                        );
                    }
                    return projectId;
                } catch (error) {
                    console.error('Error creating project:', error);
                    notifications?.onError?.(
                        error instanceof Error ? error.message : 'Fejl ved oprettelse af projekt',
                    );
                    throw error;
                }
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.PROJECTS] });
                queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.PROJECT_LOOKUPS] });
                queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.CALENDAR_PROJECTS] });
                queryClient.invalidateQueries({ queryKey: ['NEXT_CASE_NUMBER'] });
                // This call can create a delprojekt too (parentProjectId above). The parent's
                // Delprojekter tab reads its own cache key, and the parent record itself now
                // carries a different childCount — the same pair useCreateSubProject invalidates.
                queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.PROJECT] });
                queryClient.invalidateQueries({ queryKey: ['CHILD_PROJECTS'] });
                notifications?.onSuccess?.('Projekt oprettet');
            },
        });
    };

    const useFetchProject = (projectId: string) => {
        return useQuery({
            queryKey: [CACHE_KEYS.PROJECT, projectId],
            queryFn: async (): Promise<ProjectModel | undefined> => {
                try {
                    const apiProject = await getApiProject(projectId);
                    if (!apiProject) {
                        notifications?.onError?.(`Projekt med ID ${projectId} blev ikke fundet`);
                        return undefined;
                    }
                    return apiProject;
                } catch (error) {
                    // Real fetch failure (getApiProject throws on non-404) — surface it rather
                    // than letting the query fail silently with no user-facing signal.
                    console.error('Error fetching project:', error);
                    notifications?.onError?.('Fejl ved hentning af projekt');
                    throw error;
                }
            },
            enabled: !!projectId,
        });
    };

    /**
     * Every project, fully hydrated. This drains the whole table 200 rows at a
     * time; on a 7.500-project tenant that is 37-39 requests and ~16 MB (#2196).
     *
     * Prefer {@link useFetchProjectLookups} — one lean request — for anything
     * that only needs to render or group projects. Reach for this only when the
     * consumer genuinely reads project state, and pass `{ enabled: open }` if it
     * lives in a dialog so it does not drain on page load.
     */
    const useFetchProjects = (options?: { enabled?: boolean }) => {
        return useQuery({
            queryKey: [CACHE_KEYS.PROJECTS, selectedCompany?.id],
            // React Query aborts this signal on unmount and on query-key change, so
            // navigating away now stops the drain instead of letting it run to the end.
            queryFn: async ({ signal }): Promise<ProjectModel[]> => {
                if (!selectedCompany?.id) {
                    notifications?.onError?.('Ingen virksomhed valgt');
                    return [];
                }

                try {
                    const apiProjects = await listApiProjects({}, signal);
                    return apiProjects ?? [];
                } catch (error) {
                    // An abort is the caller losing interest, not a failure. Surfacing it
                    // as one would pop "Fejl ved hentning af projekter" on every navigation.
                    if (isAbortError(error)) {
                        throw error;
                    }
                    console.error('Error fetching projects:', error);
                    notifications?.onError?.('Fejl ved hentning af projekter');
                    return [];
                }
            },
            enabled: !!api && !!selectedCompany?.id && (options?.enabled ?? true),
        });
    };

    /**
     * One project by id for a picker's already-selected value, or `null` when it no longer
     * exists (deleted or archived). Unlike {@link useFetchProject} it never toasts and never
     * resolves `undefined` — React Query v5 turns an `undefined` result into an error, which
     * would make "deleted" indistinguishable from "the read failed".
     */
    const useFetchProjectOrNull = (projectId: string) => {
        return useQuery({
            queryKey: [CACHE_KEYS.PROJECT, projectId, 'or-null'],
            queryFn: async (): Promise<ProjectModel | null> => (await getApiProject(projectId)) ?? null,
            enabled: !!projectId && !!api && !!selectedCompany?.id,
        });
    };

    /**
     * Every project one user leads or has logged time on, fully hydrated — filtered
     * server-side (`involvedUserId`), so an employee's page reads their handful of projects
     * instead of draining the whole company. Drains that user-scoped set, so nothing is cut
     * off for a long-tenured employee.
     */
    const useFetchInvolvedProjects = (userId: string | undefined) => {
        return useQuery({
            queryKey: [CACHE_KEYS.PROJECTS, 'involved', selectedCompany?.id, userId],
            queryFn: async ({ signal }): Promise<ProjectModel[]> =>
                (await listApiProjects({ involvedUserId: userId }, signal)) ?? [],
            enabled: !!api && !!selectedCompany?.id && !!userId,
        });
    };

    /**
     * The lean counterpart to {@link useFetchProjects}: one unpaged request for
     * every non-archived project, carrying only the fields a picker renders.
     *
     * This is what the project-picker dialogs should use. It replaces a 37-39
     * request drain with a single response roughly 20x smaller per row.
     */
    const useFetchProjectLookups = (options?: { enabled?: boolean }) => {
        return useQuery({
            queryKey: [CACHE_KEYS.PROJECT_LOOKUPS, selectedCompany?.id],
            // No isAbortError branch here, unlike useFetchProjects: this is a single
            // request, and HttpClient does not wrap fetch in a try/catch, so an abort
            // rejects out of api.projects.lookup before the !response.ok branch that
            // notifies. The AbortError therefore propagates untouched and never toasts.
            queryFn: async ({ signal }): Promise<ProjectLookupDto[]> => {
                // `enabled` already covers this at runtime; the check stays because
                // `api` is `MesterplanApi | null` and TypeScript cannot narrow it from
                // the enabled flag.
                if (!api || !selectedCompany?.id) {
                    return [];
                }
                const response = await api.projects.lookup({ signal });
                if (!response.ok) {
                    // Same rule as the paged reader: a failed fetch is an error, not
                    // "no projects" — an empty picker would read as a real empty org.
                    // Notify as well as throw, so a picker that ignores isError still
                    // tells the user why it is empty.
                    const message = formatApiError(response.error, 'Fejl ved hentning af projekter');
                    notifications?.onError?.(message);
                    throw new Error(message);
                }
                return response.data;
            },
            enabled: !!api && !!selectedCompany?.id && (options?.enabled ?? true),
            // main.tsx sets a global `retry: 1`. This queryFn both notifies AND throws on
            // failure (see the comment above), so a global retry would run it twice and the
            // user would see the error toast twice for one failed load. useFetchProjects is
            // deliberately left on the default — its retry trade-off (36-39 requests) is
            // different — so this is scoped to this query only.
            retry: false,
        });
    };

    /**
     * Infinite scroll over the .NET projects list (real server-side pagination via
     * page/pageSize). This is the primary projects-list reader on web (Projects.tsx).
     */
    const useFetchProjectsInfinite = (options?: {
        pageSize?: number;
    }): UseInfiniteQueryResult<ProjectModel> => {
        const { pageSize = 20 } = options || {};
        const companyId = selectedCompany?.id;
        const apiEnabled = !!api && !!companyId;

        // Backed by React-Query's useInfiniteQuery (keyed under CACHE_KEYS.PROJECTS) so that
        // create/update/delete-project mutations' invalidateQueries([CACHE_KEYS.PROJECTS]) actually
        // refresh this list — the previous useState/manual-fetch implementation had no subscriber
        // and stayed stale until remount. (#1365)
        const query = useRqInfiniteQuery({
            queryKey: [CACHE_KEYS.PROJECTS, 'infinite', companyId, pageSize],
            enabled: apiEnabled,
            initialPageParam: 1,
            queryFn: async ({ pageParam }): Promise<{ items: ProjectModel[]; total: number; page: number; rawCount: number }> => {
                if (!api || !companyId) return { items: [], total: 0, page: pageParam, rawCount: 0 };
                const response = await api.projects.list({ page: pageParam, pageSize });
                if (!response.ok) {
                    throw new Error(response.error?.detail ?? response.error?.title ?? 'Kunne ikke hente projekter');
                }
                // Archived = soft-deleted; never surface it.
                const items = response.data.items
                    .filter((dto) => dto.status !== 'Archived')
                    .map((dto) => mapApiProject(dto, companyId));
                // rawCount is the pre-filter page size — the pagination cursor, so an
                // all-archived page (0 surfaced items) still advances rather than stalling.
                return { items, total: response.data.total, page: pageParam, rawCount: response.data.items.length };
            },
            getNextPageParam: (lastPage) => computeNextProjectsPageParam(lastPage, pageSize),
        });

        // Accumulate items across pages, deduped by id (server pages shouldn't overlap, but the
        // previous implementation deduped, so preserve that behaviour).
        const items = useMemo(() => {
            const byId = new Map<string, ProjectModel>();
            for (const page of query.data?.pages ?? []) {
                for (const project of page.items) byId.set(project.id, project);
            }
            return Array.from(byId.values());
        }, [query.data]);

        return {
            items,
            isLoading: query.isLoading,
            isLoadingMore: query.isFetchingNextPage,
            // Derive from the query cursor, not items.length < total: `total` counts archived
            // rows that `items` filters out, so once every non-archived row is fetched
            // items.length < total can stay true even though there are no more pages. (PR review)
            hasMore: query.hasNextPage ?? false,
            loadMore: async () => {
                if (query.hasNextPage && !query.isFetchingNextPage) {
                    await query.fetchNextPage();
                }
            },
            refresh: () => {
                void query.refetch();
            },
            error: (query.error as Error | null) ?? null,
            loadedCount: items.length,
        };
    };

    /**
     * Paged, server-searched projects for pickers. Unlike {@link useFetchProjects} and
     * {@link useFetchProjectLookups} this never loads the whole company: page 1 (server order,
     * newest first) when the picker opens, the next page on `fetchNextPage()` (infinite
     * scroll), and a fresh page 1 whenever `search`/`parentOnly`/`statuses` change. Callers
     * debounce `search`.
     *
     * The key starts with CACHE_KEYS.PROJECTS, so every project mutation's invalidation
     * refreshes it too.
     */
    const useProjectSearch = ({
        search,
        enabled,
        pageSize = PROJECT_PICKER_PAGE_SIZE,
        parentOnly = false,
        statuses,
    }: UseProjectSearchOptions) => {
        const term = search.trim();
        const companyId = selectedCompany?.id;
        const apiStatuses = mapModelStatusesToApi(statuses ?? []);
        return useRqInfiniteQuery({
            queryKey: [CACHE_KEYS.PROJECTS, 'search', companyId, term, pageSize, parentOnly, apiStatuses.join(',')],
            queryFn: async ({ pageParam, signal }): Promise<ProjectSearchPage> => {
                if (!api || !companyId) {
                    throw new Error('Ingen API-forbindelse');
                }
                const query = buildProjectSearchQuery({ page: pageParam, pageSize, term, parentOnly, apiStatuses });
                // The signal lets React Query abort a superseded search instead of letting
                // every keystroke's request run to completion.
                const response = await api.projects.list(query, { signal });
                if (!response.ok) {
                    throw new Error(formatApiError(response.error, 'Fejl ved hentning af projekter'));
                }
                // Archived = soft-deleted; never surface it in a picker.
                const items = response.data.items
                    .filter((dto) => dto.status !== 'Archived')
                    .map((dto) => mapApiProject(dto, companyId));
                return {
                    items,
                    page: response.data.page,
                    // Trust the server's echoed pageSize: it clamps, and the next-page math must match.
                    pageSize: response.data.pageSize > 0 ? response.data.pageSize : pageSize,
                    total: response.data.total,
                    rawCount: response.data.items.length,
                };
            },
            initialPageParam: 1,
            // Keep showing the previous results while the next search term loads, instead of
            // flashing an empty/loading list on every keystroke.
            placeholderData: keepPreviousData,
            getNextPageParam: (last) => computeNextProjectsPageParam(last, last.pageSize),
            enabled: enabled && !!api && !!companyId,
        });
    };

    /**
     * Project search. .NET-backed server-side `search` over the company set.
     */
    const useSearchProjects = (
        searchQuery: string,
        options?: { perFieldLimit?: number },
    ) => {
        const trimmed = searchQuery.trim().toLowerCase();
        const perFieldLimit = options?.perFieldLimit ?? 50;
        return useQuery({
            queryKey: [CACHE_KEYS.PROJECTS, 'search', selectedCompany?.id, trimmed, perFieldLimit],
            queryFn: async (): Promise<ProjectModel[]> => {
                if (!api || !selectedCompany?.id || trimmed.length === 0) return [];
                try {
                    const results = await listApiProjects({ search: trimmed });
                    return results ?? [];
                } catch (error) {
                    console.error('Error searching projects:', error);
                    notifications?.onError?.('Fejl ved søgning i projekter');
                    return [];
                }
            },
            enabled: !!api && !!selectedCompany?.id && trimmed.length > 0,
            staleTime: 30 * 1000,
            gcTime: 5 * 60 * 1000,
        });
    };

    const useFetchCalendarProjects = (currentDate: Date) => {
        const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 3, 1);
        const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 4, 0);

        const cacheKey = `${startDate.getFullYear()}-${startDate.getMonth()}-${endDate.getFullYear()}-${endDate.getMonth()}`;

        return useQuery({
            queryKey: [CACHE_KEYS.CALENDAR_PROJECTS, selectedCompany?.id, cacheKey],
            queryFn: async (): Promise<ProjectModel[]> => {
                if (!api || !selectedCompany?.id) {
                    return [];
                }

                try {
                    const apiProjects = await listApiProjects({
                        fromDate: startDate.toISOString().slice(0, 10),
                        toDate: endDate.toISOString().slice(0, 10),
                    });
                    const allProjects = apiProjects ?? [];

                    const filteredProjects = allProjects.filter((project) => {
                        const projectStart = project.startDate;
                        if (!projectStart) return false;

                        const projectStartDate = new Date(projectStart);
                        const projectEndDate = project.endDate ? new Date(project.endDate) : projectStartDate;

                        return projectStartDate <= endDate && projectEndDate >= startDate;
                    });

                    return filteredProjects;
                } catch (error) {
                    console.error('Error fetching calendar projects:', error);
                    notifications?.onError?.('Fejl ved hentning af projekter');
                    return [];
                }
            },
            enabled: !!api && !!selectedCompany?.id,
            staleTime: 5 * 60 * 1000,
            gcTime: 30 * 60 * 1000,
            refetchOnWindowFocus: false,
        });
    };

    const useMonthProjects = (currentDate: Date) => {
        const calendarQuery = useFetchCalendarProjects(currentDate);

        const monthProjects = useMemo(() => {
            if (!calendarQuery.data) return [];

            const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
            const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

            return calendarQuery.data.filter((project) => {
                if (!project.startDate) return false;
                const projectStart = new Date(project.startDate);
                const projectEnd = project.endDate ? new Date(project.endDate) : projectStart;

                return projectStart <= monthEnd && projectEnd >= monthStart;
            });
        }, [calendarQuery.data, currentDate]);

        return {
            ...calendarQuery,
            data: monthProjects,
        };
    };

    const useProjectsByDate = (currentDate: Date) => {
        const monthQuery = useMonthProjects(currentDate);

        const projectsByDate = useMemo(() => {
            if (!monthQuery.data) return {};

            const dateMap: { [key: string]: ProjectModel[] } = {};

            monthQuery.data.forEach((project) => {
                if (!project.startDate) return;
                const startDate = new Date(project.startDate);
                const endDate = project.endDate ? new Date(project.endDate) : startDate;
                const currentDateLoop = new Date(startDate);

                while (currentDateLoop <= endDate) {
                    const dateKey = currentDateLoop.toDateString();

                    if (!dateMap[dateKey]) {
                        dateMap[dateKey] = [];
                    }

                    if (!dateMap[dateKey].some((p) => p.id === project.id)) {
                        dateMap[dateKey].push(project);
                    }

                    currentDateLoop.setDate(currentDateLoop.getDate() + 1);
                }
            });

            return dateMap;
        }, [monthQuery.data]);

        return {
            ...monthQuery,
            projectsByDate,
        };
    };

    const useUpdateProject = () => {
        return useMutation({
            mutationFn: async ({
                projectId,
                data,
                propertyLink,
                billingCustomerIdOverride,
                version,
            }: {
                projectId: string;
                data: Partial<CreateProjectModel>;
                /**
                 * Ejendom link. Its own variable rather than a field on `data` because
                 * `CreateProjectModel.propertyId` is `string | undefined` and cannot express
                 * "clear it" — the PATCH contract needs an explicit null for that, and
                 * undefined has to keep meaning "leave alone".
                 */
                propertyLink?: { propertyId: string | null; propertyUnitId: string | null };
                /**
                 * Faktureres til. Own variable for the same reason as `propertyLink`: undefined
                 * leaves the stored override alone, an explicit null clears it (invoice the customer).
                 */
                billingCustomerIdOverride?: string | null;
                /**
                 * Opt-in optimistic concurrency (#4617): the `version` the caller loaded the project
                 * at. Omit for last-write-wins.
                 */
                version?: number;
            }) => {
                if (!api) {
                    notifications?.onError?.('Fejl ved opdatering af projekt');
                    throw new Error('API client unavailable');
                }
                try {
                    const oldProject = await getApiProject(projectId);

                    // Status changes go through the dedicated changeStatus endpoint (the update
                    // command ignores status). shouldChangeProjectStatus guards on
                    // statusDefinitionId, not the stage label — see its doc comment.
                    const nextStatusDefinitionId = data.statusDefinitionId;
                    if (
                        nextStatusDefinitionId &&
                        shouldChangeProjectStatus(nextStatusDefinitionId, oldProject?.statusDefinitionId)
                    ) {
                        const statusResponse = await api.projects.changeStatus(projectId, {
                            statusDefinitionId: nextStatusDefinitionId,
                        });
                        if (!statusResponse.ok) {
                            notifications?.onError?.('Projektstatus kunne ikke opdateres');
                            throw new Error(statusResponse.error?.detail ?? statusResponse.error?.title ?? 'Projektstatus kunne ikke opdateres');
                        }
                    }

                    // Merge the partial over the current record so PUT (full replace)
                    // carries the whole field set, not just the delta.
                    const merged = { ...(oldProject ?? {}), ...data } as ProjectModel;
                    const payload: UpdateProjectPayload = {
                        title: merged.title.trim(),
                        type: mapModelTypeToApi(merged.projectType),
                        customerId: merged.clientId,
                        startDate: toDateOnly(merged.startDate),
                        endDate: toDateOnly(merged.endDate),
                        description: merged.description?.trim() || null,
                        ...priceToBudgetFields(merged.price),
                        currency: 'DKK',
                        details: toProjectDetails(merged),
                        // Spread only when the caller actually asked to change the link, so a
                        // plain save cannot blank it.
                        ...(propertyLink
                            ? {
                                  propertyId: propertyLink.propertyId,
                                  propertyUnitId: propertyLink.propertyUnitId,
                              }
                            : {}),
                        ...billToOverrideUpdateFields(billingCustomerIdOverride),
                        ...(version !== undefined ? { version } : {}),
                    };
                    const response = await api.projects.update(projectId, payload);
                    if (!response.ok) {
                        if (response.status === 409 && response.error?.code === PROJECT_VERSION_CONFLICT_CODE) {
                            queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.PROJECT, projectId] });
                            queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.PROJECTS] });
                            throw new ProjectVersionConflictError();
                        }
                        notifications?.onError?.('Fejl ved opdatering af projekt');
                        throw new Error(response.error?.detail ?? response.error?.title ?? 'Projekt kunne ikke opdateres');
                    }

                    // The backend emits the ChangeLog entry atomically inside
                    // UpdateProjectHandler / ChangeProjectStatusHandler, surfaced on
                    // the web activity feed via the .NET change-feed (no Firestore
                    // PROJECT_CHANGES write needed).
                } catch (error) {
                    if (error instanceof ProjectVersionConflictError) {
                        // The caller shows the dedicated message; no generic toast on top of it.
                        throw error;
                    }
                    console.error('Error updating project:', error);
                    notifications?.onError?.('Fejl ved opdatering af projekt');
                    throw error;
                }
            },
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.PROJECTS] });
                queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.PROJECT_LOOKUPS] });
                queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.PROJECT, variables.projectId] });
                queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.CALENDAR_PROJECTS] });
                queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.ACTIVITY_LOG] });
                notifications?.onSuccess?.('Projekt opdateret');
            },
        });
    };

    const useCreateSubProject = () => {
        return useMutation({
            mutationFn: async (data: {
                parentProject: ProjectModel;
                title: string;
                description?: string;
                notes?: string;
                status?: ProjectModel['status'];
                statusDefinitionId?: string;
                price?: number;
                hourlyRate?: number;
                startDate?: string;
                endDate?: string;
                estimatedHours?: number;
                location?: string;
                projectType?: ProjectModel['projectType'];
            }): Promise<string> => {
                if (!selectedCompany?.id) {
                    // Throw (not return) so a bare resolve doesn't fire the "Delprojekt oprettet" toast.
                    notifications?.onError?.('Ingen virksomhed valgt');
                    throw new Error('Ingen virksomhed valgt');
                }
                if (!api) {
                    notifications?.onError?.('Fejl ved oprettelse af delprojekt');
                    throw new Error('API client unavailable');
                }

                const { parentProject, ...rest } = data;

                // The server derives case number, depth, childOrder, rootProjectId and
                // the parent child-count recount; we only pass parentProjectId.
                try {
                    const payload: CreateProjectPayload = {
                        title: rest.title,
                        type: mapModelTypeToApi(rest.projectType ?? parentProject.projectType),
                        customerId: parentProject.clientId,
                        startDate: toDateOnly(rest.startDate ?? parentProject.startDate),
                        endDate: toDateOnly(rest.endDate ?? parentProject.endDate),
                        description: rest.description || null,
                        ...priceToBudgetFields(rest.price),
                        currency: 'DKK',
                        parentProjectId: parentProject.id,
                        details: toProjectDetails({
                            notes: rest.notes ?? '',
                            location: rest.location ?? parentProject.location,
                            hourlyRate: rest.hourlyRate ?? parentProject.hourlyRate,
                            estimatedHours: rest.estimatedHours,
                            price: rest.price ?? 0,
                            dineroContactGuid: parentProject.dineroContactGuid,
                            economicCustomerNumber: parentProject.economicCustomerNumber,
                        }),
                    };
                    const response = await api.projects.create(payload);
                    if (!response.ok) {
                        throw new Error(formatApiError(response.error, 'Delprojekt kunne ikke oprettes'));
                    }
                    const projectId = response.data;
                    const statusApplied = await applyInitialApiStatus(api, projectId, rest.statusDefinitionId);
                    if (!statusApplied) {
                        notifications?.onError?.(
                            'Delprojektet blev oprettet, men den valgte status kunne ikke sættes. Ret status manuelt.',
                        );
                    }
                    return projectId;
                } catch (error) {
                    console.error('Error creating sub-project:', error);
                    notifications?.onError?.(
                        error instanceof Error ? error.message : 'Fejl ved oprettelse af delprojekt',
                    );
                    throw error;
                }
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.PROJECTS] });
                queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.PROJECT_LOOKUPS] });
                queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.PROJECT] });
                queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.CALENDAR_PROJECTS] });
                queryClient.invalidateQueries({ queryKey: ['CHILD_PROJECTS'] });
                notifications?.onSuccess?.('Delprojekt oprettet');
            },
        });
    };

    const useFetchChildProjects = (parentProjectId: string | undefined) => {
        return useQuery({
            queryKey: ['CHILD_PROJECTS', parentProjectId],
            queryFn: async (): Promise<ProjectModel[]> => {
                if (!parentProjectId) return [];
                try {
                    const apiChildren = await listApiProjects({ parentProjectId });
                    return (apiChildren ?? []).sort((a, b) => (a.childOrder || 0) - (b.childOrder || 0));
                } catch (error) {
                    console.error('Error fetching child projects:', error);
                    notifications?.onError?.('Fejl ved hentning af delprojekter');
                    return [];
                }
            },
            enabled: !!parentProjectId,
        });
    };

    /**
     * Fetch projects the current user is invited to as a partner. Resolves accepted
     * project partner-invitations from the .NET API (email-scoped) and loads each
     * referenced project.
     *
     * NOTE: the legacy Firestore reader also discovered partner projects by
     * `partnerCompanyId`. The .NET partner-invitation model is email-based and has
     * no company field, so the `companyId` argument is accepted for signature
     * compatibility but no longer drives discovery (see PR notes).
     */
    const useFetchPartnerProjects = (userEmail: string | undefined, companyId?: string) => {
        return useQuery({
            queryKey: [CACHE_KEYS.PROJECTS, 'partner', userEmail, companyId],
            queryFn: async (): Promise<ProjectModel[]> => {
                if (!userEmail || !api) return [];

                const result = await api.partnerInvitations.list({
                    partnerEmail: userEmail,
                    status: 'Accepted',
                    entityType: 'project',
                });
                if (!result.ok) {
                    // An unconfirmed email is explained by the dashboard's own prompt, not a toast.
                    if (result.status === 403 && result.error.title === 'email_unverified') return [];
                    console.error('Error fetching partner invitations from .NET API:', result.error);
                    notifications?.onError?.('Fejl ved hentning af partnerprojekter');
                    return [];
                }

                const invitations = result.data as PartnerInvitationDto[];
                const projectIds = new Set<string>();
                const projects: ProjectModel[] = [];
                let droppedCount = 0;
                for (const invitation of invitations) {
                    const entityId = invitation.entityId;
                    if (!entityId || projectIds.has(entityId)) continue;
                    projectIds.add(entityId);
                    // getApiProject throws on a non-404 fetch failure; keep loading the rest of
                    // the partner projects rather than failing the whole list on one bad row.
                    try {
                        const apiProject = await getApiProject(entityId);
                        if (apiProject) {
                            projects.push(apiProject);
                        }
                    } catch (error) {
                        console.error('Error fetching partner project from .NET API:', error);
                        droppedCount += 1;
                    }
                }
                // Don't present a silently-shortened partner list as complete — surface that some
                // rows failed to load (mirrors the partial-failure signal used elsewhere).
                if (droppedCount > 0) {
                    notifications?.onError?.(
                        `${droppedCount} partnerprojekt${droppedCount === 1 ? '' : 'er'} kunne ikke indlæses.`,
                    );
                }
                return projects;
            },
            enabled: !!userEmail,
        });
    };

    const useDeleteProject = () => {
        return useMutation({
            mutationFn: async (projectId: string) => {
                if (!api) {
                    notifications?.onError?.('Fejl ved sletning af projekt');
                    throw new Error('API client unavailable');
                }
                try {
                    // .NET soft-delete (archives the project tree + recounts the
                    // parent server-side).
                    const response = await api.projects.remove(projectId);
                    if (!response.ok) {
                        notifications?.onError?.('Fejl ved sletning af projekt');
                        throw new Error(response.error?.detail ?? response.error?.title ?? 'Projekt kunne ikke slettes');
                    }
                    notifications?.onSuccess?.('Projekt slettet');
                } catch (error) {
                    console.error('Error deleting project:', error);
                    notifications?.onError?.('Fejl ved sletning af projekt');
                    throw error;
                }
            },
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.PROJECTS] });
                queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.PROJECT_LOOKUPS] });
                queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.PROJECT] });
                queryClient.invalidateQueries({ queryKey: [CACHE_KEYS.CALENDAR_PROJECTS] });
                queryClient.invalidateQueries({ queryKey: ['CHILD_PROJECTS'] });
            },
        });
    };

    return {
        useCreateProject,
        useCreateSubProject,
        useNextCaseNumber,
        useFetchProject,
        useFetchProjects,
        useFetchProjectLookups,
        useFetchPartnerProjects,
        useFetchProjectsInfinite,
        useProjectSearch,
        useFetchInvolvedProjects,
        useFetchProjectOrNull,
        useSearchProjects,
        useFetchChildProjects,
        useUpdateProject,
        useDeleteProject,
        useFetchCalendarProjects,
        useMonthProjects,
        useProjectsByDate,
    };
}
